import { Link } from 'react-router-dom'
import { Link2 } from 'lucide-react'
import { Callout } from '@/components/ui/callout'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { DetailSkeleton, EmptyState, ErrorState } from '@/components/ui/states'
import { isApiError, requestErrorMessage } from '@/helpers/apiClient'
import { useOrganization } from '@/hooks/useOrganization'
import { useGhlCompanySettings } from '@/hooks/useGoHighLevel'
import { GhlNameFormatEditor } from '@/components/gohighlevel/GhlNameFormatEditor'
import { LeadStatsSettingsEditor } from '@/components/lead-stats/LeadStatsSettingsEditor'
import { useLeadStatsSettings } from '@/hooks/useLeadStats'

/**
 * The company's GoHighLevel page (docs/gohighlevel.spec.md): what every venue
 * shares. How events created from a lead are named, so a management company's
 * venues name them alike, and what its lead stats count
 * (docs/gohighlevel-stats.spec.md). Each venue's connections are on the venue's
 * own GoHighLevel page. Organization admins only.
 */
export function GoHighLevelPage() {
  const organizationQuery = useOrganization()
  const hasGhl = organizationQuery.data?.features.includes('gohighlevel') ?? false
  const settingsQuery = useGhlCompanySettings(hasGhl)
  const statsQuery = useLeadStatsSettings(hasGhl)

  if (organizationQuery.isLoading || (hasGhl && settingsQuery.isPending)) {
    return (
      <PageContainer width="wide">
        <DetailSkeleton />
      </PageContainer>
    )
  }

  if (!hasGhl) {
    return (
      <PageContainer width="wide">
        <EmptyState
          icon={Link2}
          title="GoHighLevel isn’t turned on"
          description="Ask CSE to turn it on for your company."
        />
      </PageContainer>
    )
  }

  const header = <PageHeader title="GoHighLevel" meta={organizationQuery.data?.name} />

  if (settingsQuery.isError) {
    const unavailable = isApiError(settingsQuery.error) && settingsQuery.error.status === 503
    return (
      <PageContainer width="wide" className="space-y-6">
        {header}
        {unavailable ? (
          <Callout tone="attention" title="GoHighLevel isn’t available right now">
            Try again later.
          </Callout>
        ) : (
          <ErrorState message={requestErrorMessage(settingsQuery.error)} onRetry={() => void settingsQuery.refetch()} />
        )}
      </PageContainer>
    )
  }
  const settings = settingsQuery.data
  if (settings === undefined) return null

  return (
    <PageContainer width="wide" className="space-y-8">
      {header}

      <section className="space-y-4">
        <SectionHeader
          title="Event names"
          description="How an event created from a GoHighLevel lead is named, at every venue, so your venues name events alike."
        />
        <GhlNameFormatEditor key={`${String(settings.nameFormatIsSet)}:${settings.nameFormat}`} settings={settings} />
      </section>

      <section className="space-y-4">
        <SectionHeader
          title="Lead stats"
          description="What the numbers on each venue’s dashboard count, from its GoHighLevel leads. Venues can adjust what each number is made of in their own settings."
        />
        {statsQuery.isPending ? <DetailSkeleton /> : null}
        {statsQuery.isError ? (
          <ErrorState message={requestErrorMessage(statsQuery.error)} onRetry={() => void statsQuery.refetch()} />
        ) : null}
        {statsQuery.data === undefined ? null : <LeadStatsSettingsEditor key={statsQuery.dataUpdatedAt} settings={statsQuery.data} />}
      </section>

      <section className="space-y-2">
        <SectionHeader title="Connections" />
        <p className="m-0 text-sm text-ink-muted">
          Each venue connects its own GoHighLevel sub-accounts. Open a venue from{' '}
          <Link to="/venues/all" className="font-medium text-ink underline underline-offset-2">
            Venues
          </Link>
          , then its settings.
        </p>
      </section>
    </PageContainer>
  )
}
